import React, { useCallback, useState } from "react";
import { Box } from "@mui/material";
import { hueFor, initials } from "../utils/text";

interface Props {
  title: string;
  image?: string;
  size?: number;
}

/** Feed logo, falling back to a tinted monogram when there is none (or it 404s). */
export const SourceAvatar = React.memo(function SourceAvatar({
  title,
  image,
  size = 20,
}: Props) {
  const [failed, setFailed] = useState(false);
  const hue = hueFor(title);

  // Same cached-image race as ArticleMedia: a logo that 404s from cache never
  // fires onError, so check the node's state when it mounts.
  const attachImage = useCallback((node: HTMLImageElement | null) => {
    if (node && node.complete && node.naturalWidth === 0) setFailed(true);
  }, []);

  const base = {
    width: size,
    height: size,
    borderRadius: "50%",
    flexShrink: 0,
    objectFit: "cover" as const,
  };

  if (image && !failed) {
    return (
      <Box
        component="img"
        ref={attachImage}
        src={image}
        alt=""
        aria-hidden
        loading="lazy"
        decoding="async"
        onError={() => setFailed(true)}
        sx={{ ...base, backgroundColor: "#000" }}
      />
    );
  }

  return (
    <Box
      aria-hidden
      sx={{
        ...base,
        display: "grid",
        placeItems: "center",
        fontSize: size * 0.42,
        fontWeight: 700,
        letterSpacing: "-0.02em",
        color: `hsl(${hue} 70% 82%)`,
        backgroundColor: `hsl(${hue} 45% 24%)`,
      }}
    >
      {initials(title)}
    </Box>
  );
});
